import { useCallback, useMemo, useState } from 'react'
import { useTheme } from '@mui/material'
import { useHiddenElements } from '@editor/hooks'
import { PointShapesMesh } from '@modugen/ui-toolkit'
import { ThreeEvent } from '@react-three/fiber'
import { useEditElementStore, useModelStore, useSceneStore } from '@editorStores'
import useEmitModelClickListenerV2 from './hooks/useEmitModelClickListenerV2'

type OpeningRenderMode = {
  bodyGeometry: boolean
  outline: boolean
}

type OpeningElement = {
  id: string
  points: [number, number, number][]
}

const OpeningsMesh = () => {
  const { scenePalette } = useTheme()

  const walls = useModelStore(state => state.model.walls)
  const showWallOpenings = useModelStore(state => state.showWallOpenings)
  const sceneGuidToColor = useSceneStore(state => state.guidToColor)

  const activeOpening = useEditElementStore(state => state.activeOpening)
  const multiActiveOpenings = useEditElementStore(state => state.multiActiveOpenings)

  const hiddenElements = useHiddenElements()

  const executeListener = useEmitModelClickListenerV2()

  const [hoveredOpeningGuid, setHoveredOpeningGuid] = useState<string | null>(null)

  const { elements, openingGuidToWallGuid } = useMemo(() => {
    if (!showWallOpenings) return { elements: [] as OpeningElement[], openingGuidToWallGuid: {} }

    const mapping: Record<string, string> = {}

    const el: OpeningElement[] = walls.flatMap(wall => {
      const wallGuid = wall.guid
      const openings = (wall.openings || []).filter(opening => !opening.is_local)

      return openings.map(opening => {
        const openingGuid = opening.guid
        mapping[openingGuid] = wallGuid
        return {
          id: openingGuid,
          points: opening.shape.points.map(p => [p.x, p.y, p.z] as [number, number, number]),
        }
      })
    })

    return { elements: el, openingGuidToWallGuid: mapping }
  }, [showWallOpenings, walls])

  const selectedOpeningGuids = useMemo(() => {
    const guids = new Set(multiActiveOpenings)
    if (activeOpening) guids.add(activeOpening)
    return guids
  }, [activeOpening, multiActiveOpenings])

  const guidToRenderMode = useMemo(() => {
    if (!elements.length) return {}

    return elements.reduce((acc: Record<string, OpeningRenderMode>, el) => {
      const shouldShowOutline = el.id === hoveredOpeningGuid || selectedOpeningGuids.has(el.id)
      acc[el.id] = { bodyGeometry: true, outline: shouldShowOutline }
      return acc
    }, {} as Record<string, OpeningRenderMode>)
  }, [elements, hoveredOpeningGuid, selectedOpeningGuids])

  const handleClick = useCallback(
    (openingGuid: string, event: ThreeEvent<MouseEvent>) => {
      const wallGuid = openingGuidToWallGuid[openingGuid]
      if (!wallGuid) return

      executeListener(openingGuid, event, { type: 'opening', wallGuid })
    },
    [executeListener, openingGuidToWallGuid],
  )

  const openingColor = scenePalette.elements2d.openings as string
  const guidToColor = useMemo(() => {
    return elements.reduce((acc: Record<string, string>, el) => {
      acc[el.id] = (sceneGuidToColor[el.id] || openingColor) as string
      return acc
    }, {} as Record<string, string>)
  }, [elements, openingColor, sceneGuidToColor])

  const handlePointerOver = useCallback((openingGuid: string) => {
    setHoveredOpeningGuid(openingGuid)
  }, [])

  const handlePointerOut = useCallback((openingGuid: string) => {
    setHoveredOpeningGuid(prev => (prev === openingGuid ? null : prev))
  }, [])

  const handlePointShapesClick = useCallback(
    (openingGuid: string, event: ThreeEvent<MouseEvent>) => {
      handleClick(openingGuid, event)
    },
    [handleClick],
  )

  const meshBasicMaterialProps = useMemo(() => ({ transparent: true, opacity: 0.3 }), [])

  const guidToHidden = useMemo(() => {
    if (!elements.length) return {}

    return elements.reduce((acc: Record<string, boolean>, el) => {
      const wallGuid = openingGuidToWallGuid[el.id]
      acc[el.id] = wallGuid ? hiddenElements.has(wallGuid) : false
      return acc
    }, {} as Record<string, boolean>)
  }, [elements, hiddenElements, openingGuidToWallGuid])

  if (!showWallOpenings || elements.length === 0) return null

  return (
    <PointShapesMesh
      elements={elements}
      guidToColor={guidToColor}
      guidToHidden={guidToHidden}
      guidToRenderMode={guidToRenderMode}
      handlePointerOver={handlePointerOver}
      handlePointerOut={handlePointerOut}
      handleClick={handlePointShapesClick}
      meshBasicMaterialProps={meshBasicMaterialProps}
      outlineThickness={2}
      zOffset={0.01}
    />
  )
}

export default OpeningsMesh
